import { API_BASE_URL } from '../config';
import React, { useEffect, useState } from 'react';

const card = { padding: '1rem', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: '#f8f9fa' };
const inputStyle = { width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc', boxSizing: 'border-box' };

function kolkataToday() {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
}

function formatDate(value) {
  const day = String(value || '').slice(0, 10);
  const [year, month, date] = day.split('-');
  if (!year || !month || !date) return '—';
  return `${date}-${month}-${year}`;
}

function DataTable({ headers, rows, empty }) {
  if (!rows.length) return <p style={{ color: '#6c757d' }}>{empty}</p>;
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
        <thead>
          <tr>
            {headers.map((header) => (
              <th key={header} style={{ padding: '8px', borderBottom: '1px solid #dee2e6', textAlign: 'left', backgroundColor: '#f8f9fa' }}>{header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key}>
              {row.cells.map((cell, index) => (
                <td key={index} style={{ padding: '8px', borderBottom: '1px solid #f1f3f5' }}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function HrDashboard() {
  const [activeSection, setActiveSection] = useState(null);
  const [userPermissions, setUserPermissions] = useState({});
  const [permissionsLoading, setPermissionsLoading] = useState(true);
  const [staff, setStaff] = useState([]);
  const [query, setQuery] = useState('');
  const [dutyDate, setDutyDate] = useState(kolkataToday());
  const [duty, setDuty] = useState(null);
  const [leaves, setLeaves] = useState([]);
  const [leaveForm, setLeaveForm] = useState({ staffKey: '', startDate: kolkataToday(), endDate: kolkataToday(), reason: '' });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const hasPermission = (gadgetName, permissionType = 'VIEW') => {
    const permission = userPermissions[gadgetName];
    return permission && permission[permissionType] === true;
  };

  const authHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : null;
  };

  useEffect(() => {
    const headers = authHeaders();
    if (!headers) {
      setPermissionsLoading(false);
      return;
    }
    fetch(`${API_BASE_URL}/gadgetPermissions/my-permissions`, { headers })
      .then((response) => response.ok ? response.json() : {})
      .then((permissions) => setUserPermissions(permissions || {}))
      .catch(() => setUserPermissions({}))
      .finally(() => setPermissionsLoading(false));
  }, []);

  const loadStaff = async () => {
    const headers = authHeaders();
    const response = await fetch(`${API_BASE_URL}/hr/staff`, { headers });
    const data = await response.json();
    if (!response.ok) throw new Error(data.message || 'Could not load the staff directory.');
    setStaff(data.staff || []);
    return data.staff || [];
  };

  const loadDuty = async (date) => {
    const headers = authHeaders();
    const response = await fetch(`${API_BASE_URL}/hr/on-duty?date=${date}`, { headers });
    const data = await response.json();
    if (!response.ok) throw new Error(data.message || 'Could not load who is on duty.');
    setDuty(data);
  };

  const loadLeave = async () => {
    const headers = authHeaders();
    const response = await fetch(`${API_BASE_URL}/hr/leave`, { headers });
    const data = await response.json();
    if (!response.ok) throw new Error(data.message || 'Could not load leave.');
    setLeaves(data.leaves || []);
  };

  useEffect(() => {
    if (permissionsLoading || !activeSection) return undefined;
    let cancelled = false;
    setLoading(true);
    setError('');
    const run = async () => {
      if (activeSection === 'directory') await loadStaff();
      if (activeSection === 'duty') await loadDuty(dutyDate);
      if (activeSection === 'leave') {
        await Promise.all([loadStaff(), loadLeave()]);
      }
    };
    run()
      .catch((err) => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [activeSection, permissionsLoading]);

  const changeDutyDate = async (date) => {
    setDutyDate(date);
    setLoading(true);
    setError('');
    try {
      await loadDuty(date);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const recordLeave = async () => {
    const [staffKind, staffId] = leaveForm.staffKey.split(':');
    if (!staffId) {
      setError('Select a staff member.');
      return;
    }
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const headers = authHeaders();
      const response = await fetch(`${API_BASE_URL}/hr/leave`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          staffKind,
          staffId,
          startDate: leaveForm.startDate,
          endDate: leaveForm.endDate,
          reason: leaveForm.reason
        })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Could not record the leave.');
      setMessage(data.message || 'Leave recorded.');
      setLeaveForm((form) => ({ ...form, reason: '' }));
      await Promise.all([loadStaff(), loadLeave()]);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const cancelLeave = async (id) => {
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const headers = authHeaders();
      const response = await fetch(`${API_BASE_URL}/hr/leave/${id}/cancel`, { method: 'PATCH', headers });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Could not cancel the leave.');
      setMessage('Leave cancelled.');
      await Promise.all([loadStaff(), loadLeave()]);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const needle = query.trim().toLowerCase();
  const visibleStaff = staff.filter((person) => {
    if (!needle) return true;
    return [person.name, person.role, person.phone, person.email, person.detail, person.status].join(' ').toLowerCase().includes(needle);
  });

  const gadgets = [
    ['Staff Directory', 'directory', 'Staff directory', 'Doctors, therapists, and desk staff', 'View staff', 'Close staff', '#0f766e'],
    ['On Duty Today', 'duty', 'On duty today', 'Who is scheduled, their hours, and their bookings', 'View duty', 'Close duty', '#1d4ed8'],
    ['Leave', 'leave', 'Leave', 'Record dates and a reason, and mark the person off', 'Record leave', 'Close leave', '#9a3412']
  ];

  return (
    <div style={{ padding: '2rem', maxWidth: '1200px', margin: '0 auto' }}>
      <h2>HR Dashboard</h2>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        {gadgets.map(([permission, section, title, text, closed, open, color]) => hasPermission(permission) && (
          <div key={section} style={card}>
            <h3 style={{ margin: '0 0 1rem 0', color: '#495057' }}>{title}</h3>
            <p style={{ margin: '0 0 1rem 0', fontSize: '0.9rem', color: '#6c757d' }}>{text}</p>
            <button
              type="button"
              onClick={() => {
                const next = activeSection === section ? null : section;
                setActiveSection(next);
                setError('');
                setMessage('');
                if (next) setLoading(true);
              }}
              style={{ width: '100%', padding: '10px', backgroundColor: activeSection === section ? '#dc3545' : color, color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}
            >
              {activeSection === section ? open : closed}
            </button>
          </div>
        ))}
        {!permissionsLoading && !gadgets.some(([name]) => hasPermission(name)) && (
          <div style={{ ...card, gridColumn: '1 / -1', textAlign: 'center' }}>
            <h3>No Dashboard Access</h3>
            <p>You don't have permission to access any dashboard gadgets. Please contact your administrator.</p>
          </div>
        )}
      </div>

      {activeSection === 'directory' && hasPermission('Staff Directory') && (
        <div style={{ marginBottom: '2rem', padding: '1rem', border: '1px solid #ddd', borderRadius: '8px' }}>
          <h3 style={{ marginTop: 0 }}>Staff directory</h3>
          <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search name, role, phone, or email" style={{ ...inputStyle, marginBottom: '1rem' }} />
          {loading && <p>Loading staff...</p>}
          {error && <p style={{ color: 'red' }}>{error}</p>}
          {!loading && !error && (
            <DataTable
              headers={['Name', 'Role', 'Phone', 'Email', 'Working days', 'Status']}
              empty="No staff records match this search."
              rows={visibleStaff.map((person) => ({
                key: `${person.staffKind}:${person.staffId}`,
                cells: [
                  person.detail ? `${person.name} · ${person.detail}` : person.name,
                  person.role,
                  person.phone || '—',
                  person.email || '—',
                  person.workingDays,
                  person.status
                ]
              }))}
            />
          )}
        </div>
      )}

      {activeSection === 'duty' && hasPermission('On Duty Today') && (
        <div style={{ marginBottom: '2rem', padding: '1rem', border: '1px solid #ddd', borderRadius: '8px' }}>
          <h3 style={{ marginTop: 0 }}>On duty{duty ? ` · ${duty.weekday} ${formatDate(duty.date)}` : ''}</h3>
          <p style={{ color: '#6c757d' }}>Doctors and therapists appear when this day is one of their working days. Reception, pharmacy, finance, and HR appear when their login is active.</p>
          <input type="date" value={dutyDate} onChange={(event) => changeDutyDate(event.target.value)} style={{ ...inputStyle, maxWidth: 220, marginBottom: '1rem' }} />
          {loading && <p>Loading the duty list...</p>}
          {error && <p style={{ color: 'red' }}>{error}</p>}
          {!loading && !error && duty && (
            <DataTable
              headers={['Name', 'Role', 'Hours', 'Bookings']}
              empty="Nobody is scheduled for this day."
              rows={duty.onDuty.map((person) => ({
                key: `${person.staffKind}:${person.staffId}`,
                cells: [
                  person.detail ? `${person.name} · ${person.detail}` : person.name,
                  person.role,
                  person.hoursLabel,
                  person.bookings
                ]
              }))}
            />
          )}
        </div>
      )}

      {activeSection === 'leave' && hasPermission('Leave') && (
        <div style={{ marginBottom: '2rem', padding: '1rem', border: '1px solid #ddd', borderRadius: '8px' }}>
          <h3 style={{ marginTop: 0 }}>Leave</h3>
          {hasPermission('Leave', 'CREATE') && (
            <div style={{ display: 'grid', gap: '0.75rem', maxWidth: 640, marginBottom: '1rem' }}>
              <select value={leaveForm.staffKey} onChange={(event) => setLeaveForm((form) => ({ ...form, staffKey: event.target.value }))} style={inputStyle}>
                <option value="">Select a staff member</option>
                {staff.map((person) => (
                  <option key={`${person.staffKind}:${person.staffId}`} value={`${person.staffKind}:${person.staffId}`}>
                    {person.name} · {person.role}
                  </option>
                ))}
              </select>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                <input type="date" aria-label="Leave start" value={leaveForm.startDate} onChange={(event) => setLeaveForm((form) => ({ ...form, startDate: event.target.value }))} style={inputStyle} />
                <input type="date" aria-label="Leave end" value={leaveForm.endDate} onChange={(event) => setLeaveForm((form) => ({ ...form, endDate: event.target.value }))} style={inputStyle} />
              </div>
              <input value={leaveForm.reason} onChange={(event) => setLeaveForm((form) => ({ ...form, reason: event.target.value }))} placeholder="Reason" style={inputStyle} />
              <button type="button" onClick={recordLeave} disabled={saving} style={{ padding: '10px', backgroundColor: '#1f4d40', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>
                {saving ? 'Saving...' : 'Record leave'}
              </button>
            </div>
          )}
          {loading && <p>Loading leave...</p>}
          {message && <p style={{ color: '#198754' }}>{message}</p>}
          {error && <p style={{ color: 'red' }}>{error}</p>}
          {!loading && (
            <DataTable
              headers={['Staff', 'Role', 'From', 'To', 'Reason', 'Status', '']}
              empty="No leave has been recorded."
              rows={leaves.map((leave) => ({
                key: leave._id,
                cells: [
                  leave.STAFF_NAME,
                  leave.ROLE_NAME,
                  formatDate(leave.startDate),
                  formatDate(leave.endDate),
                  leave.REASON || '—',
                  leave.LEAVE_STATUS,
                  leave.LEAVE_STATUS === 'Approved' && hasPermission('Leave', 'UPDATE')
                    ? <button type="button" onClick={() => cancelLeave(leave._id)} disabled={saving} style={{ padding: '4px 10px', border: '1px solid #9a3412', background: 'white', color: '#9a3412', borderRadius: '4px', cursor: 'pointer' }}>Cancel</button>
                    : '—'
                ]
              }))}
            />
          )}
        </div>
      )}
    </div>
  );
}

export default HrDashboard;
